import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@blueskydataplatform/shared-ui/components/ui/alert";
import { getIsBadgeSolid, getUiBranding } from "@blueskydataplatform/shared-ui/theme/ui-branding";

import { cn } from "@/lib/utils";
import useQueryParams from "@/hooks/use-query-params";
import { useMcpClient } from "./api/get-mcp-client";

/**
 * The part of an MCP consent the registrant can't fake: client_name is whatever they typed
 * at /register, but the code can only go to the redirect URI they registered. Shown wherever
 * a click hands the software statement over (H1 4071477).
 *
 * An address outside the server's allowlist (known AI apps, loopback, known app schemes) gets
 * a loud warning instead of the quiet line. Still not a block: self-hosted agents land there.
 * `className` styles the quiet line only: callers pass muted helper text, which would mute the
 * warning too.
 */
const McpAccessDestination = ({ className }: { className?: string }) => {
  const { state } = useQueryParams();
  const { data } = useMcpClient(state);

  if (data?.redirectOrigin && !data.trustedRedirect) {
    return (
      <Alert variant="destructive" solid={getIsBadgeSolid(getUiBranding())} className="mt-3 text-left">
        <AlertCircle />
        <AlertTitle>This app isn’t one we recognise</AlertTitle>
        <AlertDescription>
          <p>
            Access will be sent to <span className="font-mono font-semibold break-all">{data.redirectOrigin}</span>.
            Whoever runs that address will be able to read your organization’s data.
          </p>
          <p>Cancel unless you set up this app yourself. If someone sent you this link, don’t allow it.</p>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={cn("space-y-1 text-sm", className)}>
      {data?.redirectOrigin && (
        <p>
          Access will be sent to <span className="font-mono font-semibold break-all">{data.redirectOrigin}</span>
        </p>
      )}
      <p className="opacity-80">Only allow this if you started the connection yourself.</p>
    </div>
  );
};

export default McpAccessDestination;
